import { useEffect, useState, useRef } from "react";
import { CircleCheckBig, TextSearch, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { OrdinalIconSimple } from "@/components/OrdinalIcon";
import { useMessageTransition } from "./useMessageTransition";

/**
 * Progress prefixes sent by ModelToolRegistry search_phrase. A label marks steps that
 * would otherwise read as an ordinary document search once the prefix is stripped.
 */
const SEARCH_PREFIXES: { prefix: string; label?: string }[] = [
  { prefix: "Searching your documents for information about:" },
  { prefix: "Searching meeting minutes for:", label: "Meeting minutes: " },
  { prefix: "Searching your documents for:" },
  { prefix: "Looking up official resources for:" },
];
const TASK_INTERVAL_MS = 2000;
const ROUND_TRANSITION_DELAY_MS = 800;
const OPTIMISTIC_INITIAL_MESSAGE = "Planning research steps...";
const OPTIMISTIC_PLANNING_STEP_ID = "optimistic-planning";

/** Messages that should NOT be captured as completed steps in the research summary. */
const IGNORED_PROGRESS_MESSAGES = new Set([
  "Encountered an issue - please try again",
  "Question violates policy and cannot be answered",
  "Sending your message...",
  "Starting research on your question...",
  "Analyzing your question...",
  "No relevant documents found - providing general guidance...",
]);

/** Map progress messages to past-tense labels for the completed state. */
const PAST_TENSE_MAP: Record<string, string> = {
  "Planning research steps": "Planned research steps",
  "Building references from approved sources": "Built references from approved sources",
  "Composing answer from sources": "Composed answer from sources",
  "Finalizing response": "Finalized response",
  "Generating detailed response": "Generated detailed response",
  "Synthesizing information from approved sources": "Synthesized information from approved sources",
  "Searching with refined parameters": "Searched with refined parameters",
  "Found relevant information - generating response": "Found relevant information",
};

function toPastTense(label: string): string {
  return PAST_TENSE_MAP[label] || label.replace(/^Searching for /, "Searched for ");
}
const PHASE_LABELS_ACTIVE = [
  "Searching your documents...",
  "Comparing multiple sources...",
  "Confirming details...",
];
const PHASE_LABELS_COMPLETED = [
  "Searched your documents",
  "Compared multiple sources",
  "Confirmed details",
];

interface SearchTask {
  id: string;
  query: string;
  status: "pending" | "active" | "completed";
}

interface PhaseSummary {
  id: string;
  activeLabel: string;
  completedLabel: string;
  completed: boolean;
}

export interface ResearchPhase {
  id: string;
  label: string;
  tasks: { id: string; query: string }[];
}

export interface ResearchSummary {
  phases: ResearchPhase[];
}

interface TaskListLoadingStateProps {
  progressMessage?: string;
  isStreamingResponse: boolean;
  streamingStartTime: number;
  onStreamingStartTimeUpdate: (time: number) => void;
  researchSummaryRef?: React.MutableRefObject<ResearchSummary | null>;
}

/** Parse and deduplicate search queries from a progress message. */
function parseQueries(message: string): string[] {
  const seen = new Set<string>();
  return message.split("\n").reduce<string[]>((acc, line) => {
    const trimmed = line.trim();
    const match = SEARCH_PREFIXES.find(({ prefix }) => trimmed.startsWith(prefix));
    if (!match) return acc;
    const query = trimmed.slice(match.prefix.length).trim().replace(/\s+/g, " ");
    const cleaned = query && `${match.label ?? ""}${query}`;
    if (cleaned && !seen.has(cleaned)) {
      seen.add(cleaned);
      acc.push(cleaned);
    }
    return acc;
  }, []);
}

function makeRoundKey(queries: string[]): string {
  return [...queries].sort().join("|");
}

function TransitionMessage({ message, isTransitioning, className }: { message: string; isTransitioning: boolean; className?: string }) {
  return (
    <div className={`relative min-h-[20px] lg:min-h-[24px] flex items-start ${className ?? ""}`}>
      <p className={`text-base lg:text-lg text-muted-foreground transition-opacity duration-300 ${isTransitioning ? "opacity-0" : "opacity-100"}`}>
        {message}
      </p>
    </div>
  );
}

export function TaskListLoadingState({
  progressMessage,
  isStreamingResponse,
  streamingStartTime,
  onStreamingStartTimeUpdate,
  researchSummaryRef,
}: TaskListLoadingStateProps) {
  const [tasks, setTasks] = useState<SearchTask[]>([]);
  const [phaseSummaries, setPhaseSummaries] = useState<PhaseSummary[]>([]);
  const [regularMessage, setRegularMessage] = useState("");

  const hasActiveTasks = tasks.some((t) => t.status !== "completed");
  const { displayedMessage, isTransitioning, reset: resetTransition } = useMessageTransition(
    regularMessage,
    { enabled: !hasActiveTasks }
  );

  const processingTimer = useRef<NodeJS.Timeout | null>(null);
  const transitionTimer = useRef<NodeJS.Timeout | null>(null);
  const pendingQueries = useRef<string[] | null>(null);
  const cardBottomRef = useRef<HTMLDivElement | null>(null);

  // Mutable round state grouped in a single ref to reduce declaration noise
  const r = useRef({
    key: "",
    queries: [] as string[],
    taskIndex: 0,
    totalTasks: 0,
    phaseCount: 0,
    wiggleStart: 0,
    lastTaskActiveTime: 0,
    hasScrolled: false,
    completedPhases: [] as ResearchPhase[],
  });

  const clearTimers = () => {
    if (processingTimer.current) {
      clearTimeout(processingTimer.current);
      processingTimer.current = null;
    }
    if (transitionTimer.current) {
      clearTimeout(transitionTimer.current);
      transitionTimer.current = null;
    }
  };

  const getCompletedLabel = (index: number) =>
    index < PHASE_LABELS_COMPLETED.length
      ? PHASE_LABELS_COMPLETED[index]
      : `Completed phase ${index + 1}`;

  const syncSummaryRef = () => {
    if (researchSummaryRef) {
      researchSummaryRef.current = { phases: r.current.completedPhases };
    }
  };

  const startOptimisticPlanningStep = () => {
    if (r.current.completedPhases.length > 0) return;

    const planningLabel = OPTIMISTIC_INITIAL_MESSAGE.replace(/\.{2,}$/, "");
    const planningPhase: ResearchPhase = {
      id: OPTIMISTIC_PLANNING_STEP_ID,
      label: toPastTense(planningLabel),
      tasks: [],
    };
    r.current.completedPhases = [planningPhase];
    syncSummaryRef();
    pushPhaseSummary(OPTIMISTIC_PLANNING_STEP_ID, OPTIMISTIC_INITIAL_MESSAGE, planningPhase.label);
  };

  /** Mark all existing phase summaries as completed and push a new active one. */
  const pushPhaseSummary = (id: string, activeLabel: string, completedLabel: string) => {
    setPhaseSummaries((prev) => [
      ...prev.map((p) => (p.completed ? p : { ...p, completed: true })),
      { id, activeLabel, completedLabel, completed: false },
    ]);
  };

  /** Mark all phase summaries as completed (e.g. when streaming ends). */
  const completeAllSummaries = () => {
    setPhaseSummaries((prev) => prev.map((p) => ({ ...p, completed: true })));
  };

  const finalizeCurrentRound = () => {
    const { queries, phaseCount } = r.current;
    if (queries.length === 0) return;

    r.current.phaseCount += 1;
    const label = getCompletedLabel(phaseCount);
    const phase: ResearchPhase = {
      id: `phase-${r.current.phaseCount}`,
      label,
      tasks: queries.map((query, i) => ({
        id: `phase-${r.current.phaseCount}-task-${i}`,
        query: toPastTense(query),
      })),
    };
    r.current.completedPhases = [...r.current.completedPhases, phase];
    syncSummaryRef();
    const activeLabel = phaseCount < PHASE_LABELS_ACTIVE.length
      ? PHASE_LABELS_ACTIVE[phaseCount]
      : `Searching again...`;
    pushPhaseSummary(phase.id, activeLabel, label);
  };

  const processNextTask = (roundKey: string) => {
    if (roundKey !== r.current.key) return;

    const { taskIndex, totalTasks } = r.current;
    const lastIndex = totalTasks - 1;
    if (totalTasks <= 0 || taskIndex >= lastIndex) return;

    setTasks((prev) =>
      prev.map((task, i) => {
        if (i === taskIndex) return { ...task, status: "completed" as const };
        if (i === taskIndex + 1) {
          if (i === lastIndex) r.current.lastTaskActiveTime = Date.now();
          return { ...task, status: "active" as const };
        }
        return task;
      })
    );

    r.current.taskIndex += 1;
    if (r.current.taskIndex < lastIndex) {
      if (processingTimer.current) clearTimeout(processingTimer.current);
      processingTimer.current = setTimeout(
        () => processNextTask(roundKey),
        TASK_INTERVAL_MS
      );
    }
  };

  const initializeRound = (queries: string[]) => {
    clearTimers();
    completeAllSummaries();
    Object.assign(r.current, {
      key: makeRoundKey(queries),
      queries,
      taskIndex: 0,
      totalTasks: queries.length,
      lastTaskActiveTime: queries.length === 1 ? Date.now() : 0,
    });

    setTasks(
      queries.map((query, i) => ({
        id: `round-${r.current.phaseCount + 1}-task-${i}`,
        query,
        status: (i === 0 ? "active" : "pending") as SearchTask["status"],
      }))
    );

    // Update ref with in-progress round for parent to access
    if (researchSummaryRef && queries.length > 0) {
      researchSummaryRef.current = {
        phases: [
          ...r.current.completedPhases,
          {
            id: `phase-${r.current.phaseCount + 1}-in-progress`,
            label: getCompletedLabel(r.current.phaseCount),
            tasks: queries.map((query, i) => ({
              id: `phase-${r.current.phaseCount + 1}-task-${i}`,
              query,
            })),
          },
        ],
      };
    }

    if (queries.length > 1) {
      processingTimer.current = setTimeout(
        () => processNextTask(r.current.key),
        TASK_INTERVAL_MS
      );
    }
  };

  const transitionToNextRound = (nextQueries: string[]) => {
    pendingQueries.current = nextQueries;
    clearTimers();

    const elapsed = Date.now() - (r.current.lastTaskActiveTime || Date.now());
    const delay = Math.max(0, TASK_INTERVAL_MS - elapsed) + ROUND_TRANSITION_DELAY_MS;

    // Fast-forward: complete all except last, mark last as active
    setTasks((prev) =>
      prev.map((task, i) =>
        i === prev.length - 1
          ? { ...task, status: "active" as const }
          : { ...task, status: "completed" as const }
      )
    );

    transitionTimer.current = setTimeout(() => {
      const next = pendingQueries.current;
      pendingQueries.current = null;
      if (!next) return;

      // Complete the last task
      setTasks((prev) =>
        prev.map((task, i) =>
          i === prev.length - 1
            ? { ...task, status: "completed" as const }
            : task
        )
      );

      finalizeCurrentRound();
      initializeRound(next);
    }, delay);
  };

  /** Record a non-search progress message as a standalone step in the research summary. */
  const recordProgressStep = (message: string): boolean => {
    if (IGNORED_PROGRESS_MESSAGES.has(message)) return false;

    // Strip trailing ellipsis for a cleaner label
    const baseLabel = message.replace(/\.{2,}$/, "");
    if (!baseLabel) return false;

    // Avoid duplicating consecutive identical labels
    const lastPhase = r.current.completedPhases[r.current.completedPhases.length - 1];
    if (lastPhase && lastPhase.label === toPastTense(baseLabel) && lastPhase.tasks.length === 0) return false;

    const completedLabel = toPastTense(baseLabel);
    const stepId = `step-${Date.now()}`;
    const step: ResearchPhase = {
      id: stepId,
      label: completedLabel,
      tasks: [],
    };
    r.current.completedPhases = [...r.current.completedPhases, step];
    syncSummaryRef();
    pushPhaseSummary(stepId, `${baseLabel}...`, completedLabel);
    return true;
  };

  const finalizeForNonSearch = () => {
    if (r.current.queries.length === 0 && tasks.length === 0) return;
    clearTimers();
    pendingQueries.current = null;
    finalizeCurrentRound();
    Object.assign(r.current, { key: "", queries: [], taskIndex: 0, totalTasks: 0 });
    setTasks([]);
  };

  const resetAll = () => {
    clearTimers();
    pendingQueries.current = null;
    r.current = {
      key: "", queries: [], taskIndex: 0, totalTasks: 0,
      phaseCount: 0, wiggleStart: 0, lastTaskActiveTime: 0,
      hasScrolled: false, completedPhases: [],
    };
    setTasks([]);
    setPhaseSummaries([]);
    setRegularMessage("");
    resetTransition();
  };

  // Track streaming lifecycle
  useEffect(() => {
    if (isStreamingResponse && streamingStartTime === 0) {
      const now = Date.now();
      onStreamingStartTimeUpdate(now);
      r.current.wiggleStart = now;
    } else if (isStreamingResponse && streamingStartTime > 0 && r.current.wiggleStart === 0) {
      r.current.wiggleStart = streamingStartTime;
    }

    if (isStreamingResponse) {
      startOptimisticPlanningStep();
    } else {
      // Finalize any in-progress round before reset
      if (r.current.queries.length > 0) finalizeCurrentRound();
      completeAllSummaries();
      onStreamingStartTimeUpdate(0);
      resetAll();
    }
  }, [isStreamingResponse, streamingStartTime, onStreamingStartTimeUpdate, resetTransition]);

  // Handle incoming messages
  useEffect(() => {
    if (!progressMessage) return;

    const queries = parseQueries(progressMessage);

    if (queries.length > 0) {
      const nextKey = makeRoundKey(queries);
      if (nextKey !== r.current.key) {
        if (r.current.queries.length > 0 || tasks.length > 0) {
          transitionToNextRound(queries);
        } else {
          initializeRound(queries);
        }
      }
      setRegularMessage("");
      resetTransition();
      return;
    }

    finalizeForNonSearch();
    const recordedStep = recordProgressStep(progressMessage);
    if (recordedStep) {
      setRegularMessage("");
      resetTransition();
    }
  }, [progressMessage]);

  // Cleanup on unmount
  useEffect(() => clearTimers, []);

  // Smooth scroll to bottom on initial task appearance
  useEffect(() => {
    if (cardBottomRef.current && tasks.length > 0 && !r.current.hasScrolled) {
      r.current.hasScrolled = true;
      const timer = setTimeout(() => {
        cardBottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [tasks]);

  // Derived render state
  const hasChecklist = phaseSummaries.length > 0 || tasks.length > 0;
  const searchRoundCount = r.current.phaseCount;
  const activePhaseLabel = tasks.length > 0
    ? (searchRoundCount < PHASE_LABELS_ACTIVE.length
        ? PHASE_LABELS_ACTIVE[searchRoundCount]
        : `Searching again...`)
    : "";
  const showMessageAbove = Boolean(displayedMessage) && !hasActiveTasks && phaseSummaries.length > 0;
  const showMessageBelow = Boolean(displayedMessage) && !hasActiveTasks && !hasChecklist;
  const wiggleDelay = r.current.wiggleStart
    ? `-${Math.max(0, (Date.now() - r.current.wiggleStart) / 1000)}s`
    : "0s";

  return (
    <Card className="w-full p-4 bg-secondary">
      <div className={`flex gap-4 ${hasChecklist ? "items-start" : "items-center"}`}>
        <OrdinalIconSimple className="h-6 w-6 lg:h-8 lg:w-8 flex-shrink-0 animate-logo-pulse" />
        <div className="flex-1 min-w-0 space-y-2 transition-all duration-300 ease-out">
          {showMessageAbove && (
            <TransitionMessage message={displayedMessage} isTransitioning={isTransitioning} className="mb-2" />
          )}

          {phaseSummaries.map((phase) => (
            <div key={phase.id} className="flex items-start gap-3 animate-in fade-in slide-in-from-top-1 duration-300">
              <div className="flex-shrink-0 mt-0.5 flex items-center justify-center w-6 h-6">
                {phase.completed ? (
                  <CircleCheckBig className="h-5 w-5 text-green-500 animate-in zoom-in-75 duration-200" strokeWidth={2.5} />
                ) : (
                  <Loader2 className="h-5 w-5 text-muted-foreground animate-spin" />
                )}
              </div>
              <span className="text-base lg:text-lg font-semibold text-muted-foreground">
                {phase.completed ? phase.completedLabel : phase.activeLabel}
              </span>
            </div>
          ))}

          {activePhaseLabel && (
            <div className={`pb-2 border-b border-border/70 flex items-start gap-3 ${phaseSummaries.length > 0 ? "mt-1" : ""}`}>
              <div className="flex-shrink-0 mt-0.5 flex items-center justify-center w-6 h-6">
                <Loader2 className="h-5 w-5 text-muted-foreground animate-spin" />
              </div>
              <span className="text-base lg:text-lg font-semibold text-muted-foreground">{activePhaseLabel}</span>
            </div>
          )}

          {tasks.map((task) => (
            <div key={task.id} className="flex items-start gap-3 transition-all duration-300 ease-out">
              <div className="flex-shrink-0 mt-0.5 flex items-center justify-center w-6 h-6 transition-transform duration-200">
                {task.status === "completed" ? (
                  <CircleCheckBig className="h-5 w-5 text-green-500 animate-in zoom-in-75 duration-200" strokeWidth={2.5} />
                ) : task.status === "active" ? (
                  <TextSearch className="h-5 w-5 text-muted-foreground animate-search-pulse" style={{ animationDelay: wiggleDelay }} />
                ) : (
                  <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/30 transition-opacity duration-200" />
                )}
              </div>
              <span className="text-base lg:text-lg leading-relaxed text-muted-foreground">{task.status === "completed" ? toPastTense(task.query) : task.query}</span>
            </div>
          ))}

          {showMessageBelow && (
            <TransitionMessage message={displayedMessage} isTransitioning={isTransitioning} />
          )}
        </div>
      </div>
      <div ref={cardBottomRef} />
    </Card>
  );
}
